import { PAYMENT_METHOD_LABELS, PAYMENT_METHODS, SAMPLE_STATUS_LABELS, addDaysToDay, israelDay, type PaymentMethod, type SampleStatus } from '@takumi/shared';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/form';
import { Dialog, DialogContent } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useInvalidateCrm } from '@/lib/queries';
import type { Contact } from '@/lib/types';
import { useProducts } from './DocumentEditor';

export function PaymentDialog({
  open, onOpenChange, organizationId, orderId, defaultAmount, onDone,
}: { open: boolean; onOpenChange: (o: boolean) => void; organizationId: string; orderId?: string | null; defaultAmount?: number; onDone?: () => void }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('bank_transfer');
  const [paidAt, setPaidAt] = useState(israelDay());
  const [reference, setReference] = useState('');
  const invalidate = useInvalidateCrm();
  useEffect(() => {
    if (open) {
      setAmount(defaultAmount ? String(defaultAmount) : '');
      setMethod('bank_transfer');
      setPaidAt(israelDay());
      setReference('');
    }
  }, [open, defaultAmount]);
  const save = useMutation({
    mutationFn: () => api.post(`/organizations/${organizationId}/payments`, { orderId: orderId ?? null, amount: Number(amount), method, paidAt, reference: reference || null }),
    onSuccess: () => {
      invalidate(organizationId);
      onDone?.();
      toast.success('התשלום נרשם');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!(Number(amount) > 0)) return toast.error('יש להזין סכום');
    save.mutate();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="רישום תשלום">
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <Field label="סכום (₪, כולל מע״מ)" required>{(id) => <Input id={id} type="number" min={0.01} step="0.01" autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} />}</Field>
          <Field label="אמצעי תשלום">
            {(id) => (
              <Select id={id} value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>)}
              </Select>
            )}
          </Field>
          <Field label="תאריך">{(id) => <Input id={id} type="date" value={paidAt} max={israelDay()} onChange={(e) => setPaidAt(e.target.value)} />}</Field>
          <Field label="אסמכתא">{(id) => <Input id={id} value={reference} onChange={(e) => setReference(e.target.value)} />}</Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4 sm:col-span-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" variant="gold" loading={save.isPending}>רישום</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceDialog({ open, onOpenChange, orderId, onDone }: { open: boolean; onOpenChange: (o: boolean) => void; orderId: string; onDone?: () => void }) {
  const [issueDate, setIssueDate] = useState(israelDay());
  const [dueDate, setDueDate] = useState(addDaysToDay(israelDay(), 30));
  const [provider, setProvider] = useState('');
  const [externalId, setExternalId] = useState('');
  const save = useMutation({
    mutationFn: () => api.post(`/orders/${orderId}/invoices`, { issueDate, dueDate, externalProvider: provider || null, externalId: externalId || null }),
    onSuccess: () => {
      onDone?.();
      toast.success('החשבונית נרשמה');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="רישום חשבונית" description="רישום בלבד — חשבונית מס רשמית מופקת בתוכנה המוסמכת (חשבונית ירוקה, iCount וכד׳).">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="grid gap-4 sm:grid-cols-2">
          <Field label="תאריך הפקה">{(id) => <Input id={id} type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />}</Field>
          <Field label="לתשלום עד">{(id) => <Input id={id} type="date" value={dueDate} min={issueDate} onChange={(e) => setDueDate(e.target.value)} />}</Field>
          <Field label="מערכת מפיקה">{(id) => <Input id={id} value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="לדוגמה: חשבונית ירוקה" />}</Field>
          <Field label="מספר במערכת">{(id) => <Input id={id} value={externalId} onChange={(e) => setExternalId(e.target.value)} />}</Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4 sm:col-span-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" loading={save.isPending}>רישום</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SampleDialog({
  open, onOpenChange, organizationId, contacts,
}: { open: boolean; onOpenChange: (o: boolean) => void; organizationId: string; contacts: Pick<Contact, 'id' | 'fullName' | 'isPrimary'>[] }) {
  const products = useProducts();
  const invalidate = useInvalidateCrm();
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('0.03');
  const [contactId, setContactId] = useState('');
  const [shippingMethod, setShippingMethod] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [markSent, setMarkSent] = useState(true);
  const [followupDays, setFollowupDays] = useState('5');
  useEffect(() => {
    if (open) {
      setProductId('');
      setQuantity('0.03');
      setContactId(contacts.find((c) => c.isPrimary)?.id ?? '');
      setShippingMethod('');
      setTrackingNumber('');
      setMarkSent(true);
      setFollowupDays('5');
    }
  }, [open, contacts]);
  const save = useMutation({
    mutationFn: () => api.post('/samples', {
      organizationId, productId: productId || null, contactId: contactId || null, quantity: Number(quantity), shippingMethod: shippingMethod || null,
      trackingNumber: trackingNumber || null, markSent, followupDays: Number(followupDays),
    }),
    onSuccess: () => {
      invalidate(organizationId);
      toast.success(markSent ? 'הדוגמית נרשמה כנשלחה ונקבע פולואפ' : 'הדוגמית נרשמה');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="שליחת דוגמית">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="grid gap-4 sm:grid-cols-2">
          <Field label="מוצר" className="sm:col-span-2">
            {(id) => (
              <Select id={id} value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">—</option>
                {products.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            )}
          </Field>
          <Field label="כמות (ביחידות המוצר)" hint="למשל 0.03 = 30 גרם ממוצר בק״ג">{(id) => <Input id={id} type="number" min={0.001} step="0.001" value={quantity} onChange={(e) => setQuantity(e.target.value)} />}</Field>
          <Field label="איש קשר">
            {(id) => (
              <Select id={id} value={contactId} onChange={(e) => setContactId(e.target.value)}>
                <option value="">—</option>
                {contacts.map((c) => <option key={c.id} value={c.id}>{c.fullName}</option>)}
              </Select>
            )}
          </Field>
          <Field label="שיטת משלוח">{(id) => <Input id={id} value={shippingMethod} onChange={(e) => setShippingMethod(e.target.value)} placeholder="שליח / דואר / מסירה אישית" />}</Field>
          <Field label="מספר מעקב">{(id) => <Input id={id} dir="ltr" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} />}</Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="size-4 accent-matcha-700" checked={markSent} onChange={(e) => setMarkSent(e.target.checked)} /> נשלחה כבר
          </label>
          {markSent && <Field label="פולואפ בעוד (ימים)">{(id) => <Input id={id} type="number" min={1} max={60} value={followupDays} onChange={(e) => setFollowupDays(e.target.value)} />}</Field>}
          <div className="flex justify-end gap-2 border-t border-line pt-4 sm:col-span-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" loading={save.isPending}>שמירה</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SampleStatusDialog({
  sampleId, organizationId, open, onOpenChange,
}: { sampleId: string; organizationId: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const invalidate = useInvalidateCrm();
  const [status, setStatus] = useState<SampleStatus>('delivered');
  const [feedback, setFeedback] = useState('');
  const save = useMutation({
    mutationFn: () => api.patch(`/samples/${sampleId}`, { status, feedback: feedback || null }),
    onSuccess: () => {
      invalidate(organizationId);
      toast.success('הדוגמית עודכנה');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="עדכון דוגמית">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="grid gap-4">
          <Field label="סטטוס">
            {(id) => (
              <Select id={id} value={status} onChange={(e) => setStatus(e.target.value as SampleStatus)}>
                {(['sent', 'delivered', 'feedback_received', 'converted', 'not_relevant'] as SampleStatus[]).map((s) => <option key={s} value={s}>{SAMPLE_STATUS_LABELS[s]}</option>)}
              </Select>
            )}
          </Field>
          <Field label="משוב הלקוח">{(id) => <Textarea id={id} rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} />}</Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" loading={save.isPending}>שמירה</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
